import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Edit2, Trash2, Eye, EyeOff, Save, X, 
  ArrowLeft, Loader2, BookOpen, Lock, LogOut
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import Header from './Header';
import Footer from './Footer';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'rtc_admin_pwd';

const BlogAdmin = () => {
  const navigate = useNavigate();
  const [autenticado, setAutenticado] = useState(!!sessionStorage.getItem(STORAGE_KEY));
  const [passwordInput, setPasswordInput] = useState('');
  const [verificando, setVerificando] = useState(false);

  const [articulos, setArticulos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(null);
  const [nuevoArticulo, setNuevoArticulo] = useState(false);
  const [formData, setFormData] = useState({
    titulo: '',
    slug: '',
    extracto: '',
    contenido: '',
    imagen: '',
    categoria: '',
    autor: 'Raúl - Repara Tu Cuerpo',
    publicado: true
  });
  const [guardando, setGuardando] = useState(false);

  const getPassword = () => sessionStorage.getItem(STORAGE_KEY) || '';

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'X-Admin-Password': getPassword()
  });

  useEffect(() => {
    if (autenticado) {
      cargarArticulos();
    }
  }, [autenticado]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setVerificando(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: passwordInput })
      });
      if (response.ok) {
        sessionStorage.setItem(STORAGE_KEY, passwordInput);
        setAutenticado(true);
        toast.success('Bienvenido al panel de administración');
      } else {
        toast.error('Contraseña incorrecta');
      }
    } catch (err) {
      toast.error('Error al verificar contraseña');
    } finally {
      setVerificando(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setAutenticado(false);
    setPasswordInput('');
    toast.success('Sesión cerrada');
  };

  const cargarArticulos = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/blog`);
      const data = await response.json();
      if (data.success) {
        setArticulos(data.articulos);
      }
    } catch (err) {
      console.error('Error cargando artículos:', err);
      toast.error('Error al cargar artículos');
    } finally {
      setLoading(false);
    }
  };

  const generarSlug = (titulo) => {
    return titulo
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleTituloChange = (e) => {
    const titulo = e.target.value;
    setFormData(prev => ({
      ...prev,
      titulo,
      slug: editando ? prev.slug : generarSlug(titulo)
    }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setFormData({
      titulo: '',
      slug: '',
      extracto: '',
      contenido: '',
      imagen: '',
      categoria: '',
      autor: 'Raúl - Repara Tu Cuerpo',
      publicado: true
    });
    setEditando(null);
    setNuevoArticulo(false);
  };

  const editarArticulo = (articulo) => {
    setFormData({
      titulo: articulo.titulo,
      slug: articulo.slug,
      extracto: articulo.extracto,
      contenido: articulo.contenido,
      imagen: articulo.imagen,
      categoria: articulo.categoria,
      autor: articulo.autor || 'Raúl - Repara Tu Cuerpo',
      publicado: articulo.publicado !== false
    });
    setEditando(articulo.id);
    setNuevoArticulo(false);
  };

  const handleAuthError = (response) => {
    if (response.status === 401) {
      toast.error('Sesión expirada. Por favor inicia sesión de nuevo.');
      handleLogout();
      return true;
    }
    return false;
  };

  const guardarArticulo = async (e) => {
    e.preventDefault();
    setGuardando(true);

    try {
      if (!formData.titulo || !formData.slug || !formData.extracto || !formData.contenido) {
        toast.error('Por favor completa todos los campos obligatorios');
        setGuardando(false);
        return;
      }

      let response;
      if (editando) {
        response = await fetch(`${BACKEND_URL}/api/blog/${editando}`, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify(formData)
        });
      } else {
        response = await fetch(`${BACKEND_URL}/api/blog`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify(formData)
        });
      }

      if (handleAuthError(response)) return;

      const data = await response.json();

      if (data.success || response.ok) {
        toast.success(editando ? 'Artículo actualizado' : 'Artículo creado');
        resetForm();
        cargarArticulos();
      } else {
        toast.error(data.detail || 'Error al guardar artículo');
      }
    } catch (err) {
      console.error('Error guardando artículo:', err);
      toast.error('Error al guardar artículo');
    } finally {
      setGuardando(false);
    }
  };

  const eliminarArticulo = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este artículo?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/blog/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });

      if (handleAuthError(response)) return;

      const data = await response.json();

      if (data.success) {
        toast.success('Artículo eliminado');
        cargarArticulos();
      } else {
        toast.error(data.detail || 'Error al eliminar artículo');
      }
    } catch (err) {
      console.error('Error eliminando artículo:', err);
      toast.error('Error al eliminar artículo');
    }
  };

  const togglePublicado = async (articulo) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/blog/${articulo.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ publicado: !articulo.publicado })
      });

      if (handleAuthError(response)) return;

      const data = await response.json();

      if (data.success) {
        toast.success(articulo.publicado ? 'Artículo ocultado' : 'Artículo publicado');
        cargarArticulos();
      }
    } catch (err) {
      console.error('Error actualizando artículo:', err);
      toast.error('Error al actualizar artículo');
    }
  };

  const formatearFecha = (fecha) => {
    const date = new Date(fecha);
    return date.toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Pantalla de login
  if (!autenticado) {
    return (
      <div className='min-h-screen bg-[var(--bg-page)]'>
        <Header />
        <div className='container mx-auto px-4 py-24 md:py-32'>
          <div className='max-w-md mx-auto'>
            <Card className='network-card' data-testid="admin-login-card">
              <CardHeader>
                <div className='flex items-center justify-center gap-3 mb-2'>
                  <Lock size={32} className='text-[var(--brand-dark)]' />
                  <CardTitle className='heading-2'>Acceso Admin</CardTitle>
                </div>
                <p className='body-medium text-center text-[var(--text-secondary)]'>
                  Introduce la contraseña para administrar el blog
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className='space-y-4'>
                  <div>
                    <label className='body-medium font-semibold block mb-2'>Contraseña</label>
                    <Input
                      type='password'
                      value={passwordInput}
                      onChange={(e) => setPasswordInput(e.target.value)}
                      placeholder='Introduce tu contraseña'
                      required
                      autoFocus
                      data-testid="admin-password-input"
                    />
                  </div>
                  <div className='flex gap-3'>
                    <Button 
                      type='button' 
                      variant='outline'
                      onClick={() => navigate('/')}
                      data-testid="admin-back-btn"
                    >
                      <ArrowLeft className='mr-2 h-4 w-4' />
                      Volver
                    </Button>
                    <Button 
                      type='submit' 
                      className='btn-primary flex-1'
                      disabled={verificando}
                      data-testid="admin-login-btn"
                    >
                      {verificando ? (
                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                      ) : (
                        <Lock className='mr-2 h-4 w-4' />
                      )}
                      Entrar
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-[var(--bg-page)]'>
      <Header />
      
      <div className='container mx-auto px-4 py-24 md:py-32'>
        <div className='max-w-6xl mx-auto'>
          {/* Header */}
          <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8'>
            <div className='flex items-center gap-3'>
              <Button 
                variant='ghost' 
                onClick={() => navigate('/')}
                data-testid="btn-volver-inicio"
              >
                <ArrowLeft className='h-4 w-4' />
              </Button>
              <div className='flex items-center gap-2'>
                <BookOpen size={32} className='text-[var(--brand-dark)]' />
                <h1 className='heading-1'>Administrar Blog</h1>
              </div>
            </div>
            
            <div className='flex items-center gap-2'>
              {!nuevoArticulo && !editando && (
                <Button 
                  className='btn-primary'
                  onClick={() => setNuevoArticulo(true)}
                  data-testid="btn-nuevo-articulo"
                >
                  <Plus className='mr-2 h-4 w-4' />
                  Nuevo Artículo
                </Button>
              )}
              <Button 
                variant='outline'
                onClick={handleLogout}
                data-testid="admin-logout-btn"
                title='Cerrar sesión'
              >
                <LogOut className='h-4 w-4' />
              </Button>
            </div>
          </div>

          {/* Formulario de edición/creación */}
          {(nuevoArticulo || editando) && (
            <Card className='network-card mb-8'>
              <CardHeader>
                <CardTitle className='heading-2'>
                  {editando ? 'Editar Artículo' : 'Nuevo Artículo'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={guardarArticulo} className='space-y-4'>
                  <div className='grid md:grid-cols-2 gap-4'>
                    <div>
                      <label className='body-medium font-semibold block mb-2'>Título *</label>
                      <Input
                        name='titulo'
                        value={formData.titulo}
                        onChange={handleTituloChange}
                        placeholder='Título del artículo'
                        required
                        data-testid="input-titulo"
                      />
                    </div>
                    <div>
                      <label className='body-medium font-semibold block mb-2'>Slug (URL)</label>
                      <Input
                        name='slug'
                        value={formData.slug}
                        onChange={handleInputChange}
                        placeholder='url-del-articulo'
                        data-testid="input-slug"
                      />
                    </div>
                  </div>

                  <div className='grid md:grid-cols-2 gap-4'>
                    <div>
                      <label className='body-medium font-semibold block mb-2'>Categoría *</label>
                      <Input
                        name='categoria'
                        value={formData.categoria}
                        onChange={handleInputChange}
                        placeholder='Ej: Dolor de Espalda'
                        required
                        data-testid="input-categoria"
                      />
                    </div>
                    <div>
                      <label className='body-medium font-semibold block mb-2'>Autor</label>
                      <Input
                        name='autor'
                        value={formData.autor}
                        onChange={handleInputChange}
                        placeholder='Nombre del autor'
                        data-testid="input-autor"
                      />
                    </div>
                  </div>

                  <div>
                    <label className='body-medium font-semibold block mb-2'>URL de Imagen</label>
                    <div className='flex gap-2'>
                      <Input
                        name='imagen'
                        value={formData.imagen}
                        onChange={handleInputChange}
                        placeholder='https://ejemplo.com/imagen.jpg'
                        className='flex-1'
                        data-testid="input-imagen"
                      />
                      {formData.imagen && (
                        <div className='w-16 h-10 rounded overflow-hidden bg-gray-100'>
                          <img 
                            src={formData.imagen} 
                            alt='Preview' 
                            className='w-full h-full object-cover'
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className='body-medium font-semibold block mb-2'>Extracto *</label>
                    <textarea
                      name='extracto'
                      value={formData.extracto}
                      onChange={handleInputChange}
                      placeholder='Breve descripción del artículo (máx. 200 caracteres)'
                      className='w-full p-3 rounded-lg border border-[var(--border-light)] bg-white min-h-[80px] resize-none'
                      required
                      data-testid="input-extracto"
                    />
                  </div>

                  <div>
                    <label className='body-medium font-semibold block mb-2'>Contenido *</label>
                    <textarea
                      name='contenido'
                      value={formData.contenido}
                      onChange={handleInputChange}
                      placeholder={'Contenido del artículo.\n\nUsa ## para títulos y - para listas'}
                      className='w-full p-3 rounded-lg border border-[var(--border-light)] bg-white min-h-[300px] font-mono text-sm'
                      required
                      data-testid="input-contenido"
                    />
                    <p className='body-small text-[var(--text-light)] mt-1'>
                      Formato: ## Título, ### Subtítulo, - Lista, 1. Lista numerada
                    </p>
                  </div>

                  <div className='flex items-center gap-2'>
                    <input
                      type='checkbox'
                      name='publicado'
                      id='publicado'
                      checked={formData.publicado}
                      onChange={handleInputChange}
                      className='w-4 h-4'
                    />
                    <label htmlFor='publicado' className='body-medium'>Publicar artículo</label>
                  </div>

                  <div className='flex gap-3 pt-4'>
                    <Button 
                      type='submit' 
                      className='btn-primary'
                      disabled={guardando}
                      data-testid="btn-guardar-articulo"
                    >
                      {guardando ? (
                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                      ) : (
                        <Save className='mr-2 h-4 w-4' />
                      )}
                      {editando ? 'Actualizar' : 'Guardar'}
                    </Button>
                    <Button 
                      type='button' 
                      variant='outline'
                      onClick={resetForm}
                      data-testid="btn-cancelar"
                    >
                      <X className='mr-2 h-4 w-4' />
                      Cancelar
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Lista de artículos */}
          <Card className='network-card'>
            <CardHeader>
              <CardTitle className='heading-2'>
                Artículos ({articulos.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className='flex justify-center items-center py-12'>
                  <Loader2 className='animate-spin text-[var(--brand-dark)]' size={48} />
                </div>
              ) : articulos.length === 0 ? (
                <div className='text-center py-12'>
                  <BookOpen size={48} className='mx-auto text-[var(--text-light)] mb-4' />
                  <p className='body-large text-[var(--text-light)]'>
                    No hay artículos todavía.
                  </p>
                  <Button 
                    className='btn-primary mt-4'
                    onClick={() => setNuevoArticulo(true)}
                  >
                    <Plus className='mr-2 h-4 w-4' />
                    Crear primer artículo
                  </Button>
                </div>
              ) : (
                <div className='space-y-4'>
                  {articulos.map((articulo) => (
                    <div 
                      key={articulo.id}
                      className={`flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 rounded-xl border ${
                        articulo.publicado 
                          ? 'border-[var(--border-light)] bg-white' 
                          : 'border-orange-200 bg-orange-50'
                      }`}
                      data-testid={`articulo-item-${articulo.slug}`}
                    >
                      <div className='flex items-start gap-4 flex-1'>
                        {articulo.imagen && (
                          <div className='w-20 h-14 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 hidden sm:block'>
                            <img 
                              src={articulo.imagen} 
                              alt={articulo.titulo}
                              className='w-full h-full object-cover'
                            />
                          </div>
                        )}
                        <div className='flex-1 min-w-0'>
                          <div className='flex items-center gap-2 mb-1'>
                            <h3 className='body-medium font-semibold truncate'>{articulo.titulo}</h3>
                            {!articulo.publicado && (
                              <span className='text-xs bg-orange-200 text-orange-800 px-2 py-0.5 rounded'>
                                Borrador
                              </span>
                            )}
                          </div>
                          <div className='flex flex-wrap items-center gap-2 text-sm text-[var(--text-light)]'>
                            <span className='bg-[var(--bg-subtle)] px-2 py-0.5 rounded'>
                              {articulo.categoria}
                            </span>
                            <span>•</span>
                            <span>{formatearFecha(articulo.created_at)}</span>
                          </div>
                        </div>
                      </div>

                      <div className='flex items-center gap-2'>
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={() => navigate(`/blog/${articulo.slug}`)}
                          title='Ver artículo'
                          data-testid={`btn-ver-${articulo.slug}`}
                        >
                          <Eye className='h-4 w-4' />
                        </Button>
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={() => togglePublicado(articulo)}
                          title={articulo.publicado ? 'Ocultar' : 'Publicar'}
                          data-testid={`btn-toggle-${articulo.slug}`}
                        >
                          {articulo.publicado ? (
                            <EyeOff className='h-4 w-4' />
                          ) : (
                            <Eye className='h-4 w-4 text-green-600' />
                          )}
                        </Button>
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={() => editarArticulo(articulo)}
                          title='Editar'
                          data-testid={`btn-editar-${articulo.slug}`}
                        >
                          <Edit2 className='h-4 w-4' />
                        </Button>
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={() => eliminarArticulo(articulo.id)}
                          title='Eliminar'
                          className='text-red-600 hover:text-red-700 hover:bg-red-50'
                          data-testid={`btn-eliminar-${articulo.slug}`}
                        >
                          <Trash2 className='h-4 w-4' />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default BlogAdmin;
